/**
 * @file serviceCostCatalog.ts
 * @brief Vendor grouping, human names and plain units for `pricing_config`.
 *
 * @details
 * `pricing_config` stores one row per service key, `cost_per_unit_cents`
 * in US cents (vendors bill in USD). This file only describes the rows for
 * people: which vendor bills them, what the service is, what the unit means
 * and what one unit costs us per hour of real use. It never holds a rate.
 *
 * `MISSING_RATES` lists vendor costs we pay today that have NO row, so the
 * per-step COGS ledger cannot price them from here. They show as "Not set"
 * and are read-only: the PUT route inserts unknown keys with a guessed unit
 * (`per_1m_tokens`), so the web must never send one. The backend adds them.
 *
 * Key → model mapping: BACKEND/src/features/billing/cost.service.mjs.
 * Seeded rows: BACKEND/src/db/migrations/ensure_pricing_catalog.js.
 *
 * @author DES
 * @date 22-10-2026
 */

export type CostVendorId =
  | "openai"
  | "deepgram"
  | "soniox"
  | "azure"
  | "google"
  | "stripe"
  | "other";

export const COST_VENDORS: { id: CostVendorId; label: string }[] = [
  { id: "openai", label: "OpenAI" },
  { id: "deepgram", label: "Deepgram" },
  { id: "soniox", label: "Soniox" },
  { id: "azure", label: "Azure" },
  { id: "google", label: "Google" },
  { id: "stripe", label: "Stripe" },
  { id: "other", label: "Other" },
];

/** What one unit of a row turns into for us, for the "≈ £ per hour" line. */
export type UsageBasis =
  | "meeting_hour" // per-minute ASR: 60 min of meeting audio
  | "interpret_hour" // per-minute realtime: 60 min of interpretation
  | "speech_hour" // per-char TTS: ~14 chars/s of speech = 50,400 chars
  | "none";

export interface ServiceMeta {
  vendor: CostVendorId;
  name: string;
  basis: UsageBasis;
  /** Short grey note under the name (e.g. "legacy", "not used"). */
  note?: string;
}

export const SERVICE_META: Record<string, ServiceMeta> = {
  // ── OpenAI ────────────────────────────────────────────────────────
  whisper_asr: { vendor: "openai", name: "Speech to text (OpenAI Whisper-1)", basis: "meeting_hour" },
  gpt4o_mini_transcribe: { vendor: "openai", name: "Speech to text (OpenAI GPT-4o mini transcribe)", basis: "meeting_hour" },
  whisper: { vendor: "openai", name: "Speech to text (OpenAI Whisper)", basis: "meeting_hour", note: "Legacy row" },
  gpt4mini_input: { vendor: "openai", name: "Text model GPT-4o mini, input (OpenAI)", basis: "none" },
  gpt4mini_output: { vendor: "openai", name: "Text model GPT-4o mini, output (OpenAI)", basis: "none" },
  gpt4o_input: { vendor: "openai", name: "Text model GPT-4o, input (OpenAI)", basis: "none" },
  gpt4o_output: { vendor: "openai", name: "Text model GPT-4o, output (OpenAI)", basis: "none" },
  gpt41_input: { vendor: "openai", name: "Text model GPT-4.1, input (OpenAI)", basis: "none" },
  gpt41_output: { vendor: "openai", name: "Text model GPT-4.1, output (OpenAI)", basis: "none" },
  gpt41mini_input: { vendor: "openai", name: "Text model GPT-4.1 mini, input (OpenAI)", basis: "none" },
  gpt41mini_output: { vendor: "openai", name: "Text model GPT-4.1 mini, output (OpenAI)", basis: "none" },
  gpt41nano_input: { vendor: "openai", name: "Text model GPT-4.1 nano, input (OpenAI)", basis: "none" },
  gpt41nano_output: { vendor: "openai", name: "Text model GPT-4.1 nano, output (OpenAI)", basis: "none" },
  gpt4_input: { vendor: "openai", name: "Text model GPT-4, input (OpenAI)", basis: "none", note: "Legacy row" },
  gpt4_output: { vendor: "openai", name: "Text model GPT-4, output (OpenAI)", basis: "none", note: "Legacy row" },
  gpt4omini_tts_text: { vendor: "openai", name: "Text to speech GPT-4o mini TTS, text in (OpenAI)", basis: "none" },
  gpt4omini_tts_audio: { vendor: "openai", name: "Text to speech GPT-4o mini TTS, audio out (OpenAI)", basis: "none" },
  tts1_chars: { vendor: "openai", name: "Text to speech TTS-1 (OpenAI)", basis: "speech_hour" },
  tts1hd_chars: { vendor: "openai", name: "Text to speech TTS-1 HD (OpenAI)", basis: "speech_hour" },
  tts: { vendor: "openai", name: "Text to speech (OpenAI)", basis: "speech_hour", note: "Legacy row" },
  emb3_small: { vendor: "openai", name: "Embeddings, small (OpenAI)", basis: "none" },
  emb3_large: { vendor: "openai", name: "Embeddings, large (OpenAI)", basis: "none" },
  gpt_realtime_translate: { vendor: "openai", name: "Realtime translation (OpenAI gpt-realtime-translate)", basis: "interpret_hour" },
  gpt_realtime_whisper: { vendor: "openai", name: "Realtime transcription (OpenAI gpt-realtime-whisper)", basis: "interpret_hour" },

  // ── Deepgram ──────────────────────────────────────────────────────
  deepgram_nova: { vendor: "deepgram", name: "Speech to text, one language (Deepgram Nova)", basis: "meeting_hour" },
  deepgram_nova_multi: { vendor: "deepgram", name: "Speech to text, multilingual (Deepgram Nova)", basis: "meeting_hour" },
  deepgram_aura_chars: { vendor: "deepgram", name: "Text to speech (Deepgram Aura)", basis: "speech_hour" },
  deepgram_diarize: { vendor: "deepgram", name: "Speaker labels add-on (Deepgram)", basis: "meeting_hour", note: "Not used: included in Pre-Recorded" },

  // ── Soniox ────────────────────────────────────────────────────────
  soniox_rt: { vendor: "soniox", name: "Real-time speech to text, fallback (Soniox)", basis: "interpret_hour", note: "Used only when a turn has no token count" },
  soniox_tts_chars: { vendor: "soniox", name: "Text to speech (Soniox)", basis: "speech_hour", note: "Estimate: Soniox bills text + audio tokens" },

  // ── Azure / Google ────────────────────────────────────────────────
  azure_tts_chars: { vendor: "azure", name: "Text to speech (Azure)", basis: "speech_hour" },
  google_tts_chars: { vendor: "google", name: "Text to speech (Google Chirp 3 HD)", basis: "speech_hour" },
};

/**
 * Vendor costs with no `pricing_config` row today. Facts only: where the
 * number lives instead (a code constant, nowhere), never a made-up rate.
 */
export interface MissingRate {
  key: string; // proposed key for the backend
  vendor: CostVendorId;
  name: string;
  unit: string;
  why: string;
}

export const MISSING_RATES: MissingRate[] = [
  {
    key: "soniox_rt_audio / soniox_rt_text_out",
    vendor: "soniox",
    name: "Real-time speech to text, per token (Soniox)",
    unit: "$ per 1M tokens",
    why: "Interpretation turns are priced from a code constant (sonioxRealtime.mjs), not a row.",
  },
  {
    key: "soniox_async_audio / soniox_async_text",
    vendor: "soniox",
    name: "Batch speech to text (Soniox)",
    unit: "$ per 1M tokens",
    why: "Meeting fallback chain is priced from a code constant (soniox.mjs), not a row.",
  },
  {
    key: "soniox_translation",
    vendor: "soniox",
    name: "Translation (Soniox)",
    unit: "$ per 1M tokens",
    why: "Backend treats it as part of real-time text-out tokens; no row of its own.",
  },
  {
    key: "stripe_card_fee",
    vendor: "stripe",
    name: "Card processing fee (Stripe)",
    unit: "% + fixed per charge",
    why: "Not tracked anywhere in the cost ledger.",
  },
  {
    key: "stripe_tax_fee",
    vendor: "stripe",
    name: "Stripe Tax (Stripe)",
    unit: "% of taxed volume",
    why: "Not tracked anywhere in the cost ledger.",
  },
  {
    key: "gcp_cloud_run",
    vendor: "google",
    name: "Cloud Run, backend hosting (Google Cloud)",
    unit: "$ per month",
    why: "Infra cost, not tracked in the cost ledger.",
  },
  {
    key: "gcp_cloud_sql",
    vendor: "google",
    name: "Cloud SQL, database (Google Cloud)",
    unit: "$ per month",
    why: "Infra cost, not tracked in the cost ledger.",
  },
  {
    key: "assemblyai_universal",
    vendor: "other",
    name: "Speech to text (AssemblyAI Universal)",
    unit: "$ per hour",
    why: "ASR fallback chain is priced from a code constant (assemblyai.mjs), not a row.",
  },
];

/** Vendor for a key: the catalog first, then the key's prefix. */
export function vendorForKey(key: string): CostVendorId {
  const known = SERVICE_META[key];
  if (known) return known.vendor;
  if (key.startsWith("azure_")) return "azure";
  if (key.startsWith("deepgram_")) return "deepgram";
  if (key.startsWith("soniox_")) return "soniox";
  if (key.startsWith("google_")) return "google";
  if (key.startsWith("stripe_")) return "stripe";
  if (/^(gpt|emb|whisper|tts)/.test(key)) return "openai";
  return "other";
}

/** Plain unit, in dollars (rows are stored in US cents). */
export function unitLabel(unitType: string): string {
  switch (unitType) {
    case "per_minute":
      return "$ per minute";
    case "per_1m_tokens":
      return "$ per 1M tokens";
    case "per_1m_chars":
      return "$ per 1M characters";
    case "per_1m_audio_tokens":
      return "$ per 1M audio tokens";
    default:
      return unitType;
  }
}

/**
 * GBP per USD for the "≈ £" hints only. Same value the backend falls back to
 * (admin/service.js getUsdToGbpRate). Never used to store anything.
 */
export const HINT_GBP_PER_USD = 0.79;

/** ~14 chars/s of speech (same assumption as the soniox_tts_chars seed). */
const SPEECH_CHARS_PER_HOUR = 14 * 3600;

/** "≈ £0.19 per hour of meeting" for one row, or null when it does not apply. */
export function usageHint(
  basis: UsageBasis,
  unitType: string,
  cents: number,
): string | null {
  if (!Number.isFinite(cents) || cents < 0) return null;
  const usd = cents / 100;
  let perHourUsd: number | null = null;
  let label = "";
  if (unitType === "per_minute" && basis !== "none" && basis !== "speech_hour") {
    perHourUsd = usd * 60;
    label = basis === "interpret_hour" ? "per hour of interpretation" : "per hour of meeting";
  } else if (unitType === "per_1m_chars" && basis === "speech_hour") {
    perHourUsd = (usd * SPEECH_CHARS_PER_HOUR) / 1_000_000;
    label = "per hour of speech";
  }
  if (perHourUsd === null) return null;
  return `≈ ${formatGbp(perHourUsd * HINT_GBP_PER_USD)} ${label}`;
}

function formatGbp(gbp: number): string {
  if (gbp === 0) return "£0";
  return gbp < 0.01 ? `£${gbp.toFixed(4)}` : `£${gbp.toFixed(2)}`;
}

/** Cents (stored) → dollars string for the input, without float noise. */
export function centsToDollarString(cents: number): string {
  return String(Number((cents / 100).toPrecision(12)));
}

/** Dollars string → cents (stored), or null when not a valid amount. */
export function dollarStringToCents(value: string): number | null {
  const trimmed = value.trim().replace(/^\$/, "");
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Number((n * 100).toPrecision(12));
}
